import React, { useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemButton,
  Typography,
  Skeleton,
  Alert,
  IconButton,
} from '@mui/material';
import {
  StickyNote2 as NotesIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNotesStore } from '../../store/notes';

const RecentNotesWidget: React.FC = () => {
  const { notes, loading, error, fetchNotes } = useNotesStore();
  const navigate = useNavigate();

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const recentNotes = React.useMemo(
    () =>
      [...notes]
        .sort((a, b) => new Date(b.modified).getTime() - new Date(a.modified).getTime())
        .slice(0, 5),
    [notes]
  );

  const handleClick = (id: string) => {
    navigate(`/notes?open=${id}`);
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const summarizeTags = (tags: string[]) => {
    if (tags.length <= 2) {
      return tags.join(', ');
    }
    return `${tags.slice(0, 2).join(', ')} +${tags.length - 2} more`;
  };

  return (
    <Card
      sx={{
        height: { xs: 'auto', md: '100%' },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardHeader
        title="Recent Notes"
        avatar={<NotesIcon />}
        action={
          <IconButton
            onClick={fetchNotes}
            disabled={loading}
            aria-label="Refresh notes"
            sx={{
              width: { xs: 44, md: 32 },
              height: { xs: 44, md: 32 },
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        }
        titleTypographyProps={{ variant: 'h6' }}
        sx={{
          pb: 0,
          flexShrink: 0,
          '& .MuiCardHeader-content': { minWidth: 0 },
          '& .MuiCardHeader-action': { m: 0, alignSelf: 'center' },
        }}
      />
      <CardContent
        sx={{
          pt: 1,
          flexGrow: { md: 1 },
          minHeight: 0,
          overflowY: { xs: 'visible', md: 'auto' },
        }}
      >
        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} variant="text" height={48} />
          ))
        ) : recentNotes.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No notes yet
          </Typography>
        ) : (
          <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {recentNotes.map((note) => (
              <ListItem key={note.id} disablePadding>
                <ListItemButton
                  onClick={() => handleClick(note.id)}
                  alignItems="flex-start"
                  sx={{
                    borderRadius: 1.5,
                    px: { xs: 1.25, md: 1 },
                    py: { xs: 1.25, md: 0.875 },
                  }}
                >
                  <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {note.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Edited {formatDate(note.modified)}
                    </Typography>
                    {note.tags.length > 0 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: '-webkit-box',
                          mt: 0.25,
                          overflow: 'hidden',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {summarizeTags(note.tags)}
                      </Typography>
                    )}
                  </Box>
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentNotesWidget;
